import { GateTypes, Registration } from '@/types/registration.types';

/**
 * Resolves the set of ACTIVE gate types for DISPLAY in the gating panel.
 *
 * brandlive-platform's write path always sets `project_gate_type` to a GateTypes value or `null`
 * (never `undefined` — see derive-registration-update.ts `applyGateMutation`). So:
 *  - defined (incl. `null`) -> brandlive-platform authored this gating; it's the single source of
 *    truth (only one gate at a time). Return `[gate]`, or `[]` for an explicit `null` ("open").
 *  - `undefined`            -> legacy events-3 data that never went through brandlive-platform.
 *    events-3 allowed multiple gates to coexist (e.g. audience list + blocked domains), so return
 *    EVERY configured gate, highest-precedence first. The order is also used to pick the survivor
 *    when the user collapses the set to a single gate (see resolve-gate-toggle.ts).
 *
 * DISPLAY-ONLY: callers must NOT persist the result on load. events-api
 * `apply-effective-requirements` no-ops while `project_gate_type === undefined`, which is what lets
 * legacy events keep working on the attendee site untouched. Persistence only happens when the user
 * makes an explicit gating change, which sets `project_gate_type` through the normal write path.
 */
export default function deriveActiveGateTypes(settings: Registration | undefined): GateTypes[] {
  if (!settings) return [];

  // Respect any value brandlive-platform has authored (null = explicit "open").
  if (settings.project_gate_type !== undefined) {
    return settings.project_gate_type === null ? [] : [settings.project_gate_type];
  }

  // ---- Legacy derivation (project_gate_type === undefined) ----
  // Precedence mirrors events-3's mutual-exclusivity: password gating cleared all other
  // requirements, so it dominates; then audience lists, shared passcode, and finally the
  // domain gate (allow list or a standalone block list). The domain allow/block sub-mode is
  // resolved separately by the existing `domain_gate_mode` heuristic in the consuming components.
  const requirements = settings.requirements;
  const gates: GateTypes[] = [];

  if (settings.password_gating_enabled === true) gates.push(GateTypes.password);
  if (requirements?.[GateTypes.passcode_list]?.length) gates.push(GateTypes.passcode_list);
  if (requirements?.[GateTypes.shared_passcode]?.sharedPasscode?.trim()) gates.push(GateTypes.shared_passcode);
  if (requirements?.[GateTypes.domain_email]?.allowedDomains?.length || settings.blockedDomainEmails?.length) {
    gates.push(GateTypes.domain_email);
  }

  return gates;
}
